import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "./ScrollEffects";
import { Sparkles } from "lucide-react";

interface ProblemItem {
  emoji: string;
  problem: string;
  solution: string;
}

const cardAccents = [
  "from-[hsl(25,90%,60%)] to-[hsl(35,95%,65%)]",
  "from-[hsl(350,75%,55%)] to-[hsl(15,85%,60%)]",
  "from-[hsl(40,90%,55%)] to-[hsl(50,95%,60%)]",
  "from-[hsl(280,60%,55%)] to-[hsl(310,70%,60%)]",
];

export const ProblemSolutionSection: React.FC = () => {
  const { t } = useTranslation();
  const items = t('problem_solution.problems', { returnObjects: true }) as ProblemItem[];

  return (
    <section className="py-24 sm:py-32 px-4 relative overflow-hidden">
      <div className="max-w-5xl mx-auto relative z-10">
        <ScrollReveal className="text-center mb-16">
          <motion.span
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-[0.15em] mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <Sparkles size={12} />
            {t('problem_solution.section_badge')}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-4 leading-tight">
            {t('problem_solution.title_prefix')}{" "}
            <span className="text-primary">{t('problem_solution.title_highlight')}</span>
          </h2>
        </ScrollReveal>

        <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
          {Array.isArray(items) && items.map((item, i) => (
            <motion.div
              key={i}
              className="group relative"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -4 }}
            >
              {/* Card */}
              <div
                className="relative rounded-3xl bg-card overflow-hidden transition-shadow duration-500"
                style={{
                  boxShadow: "0 1px 3px hsl(var(--foreground) / 0.04), 0 8px 24px hsl(var(--foreground) / 0.06)",
                }}
              >
                {/* Top accent bar */}
                <div className={`h-1 bg-gradient-to-r ${cardAccents[i % cardAccents.length]}`} />

                {/* Inner glow on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.03] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative p-8 sm:p-9">
                  {/* Emoji with subtle bg circle */}
                  <div className="mb-6">
                    <motion.div
                      className="w-14 h-14 rounded-2xl bg-primary/[0.08] flex items-center justify-center"
                      whileHover={{ scale: 1.1, rotate: [0, -6, 6, 0] }}
                      transition={{ duration: 0.4 }}
                    >
                      <span className="text-3xl">{item.emoji}</span>
                    </motion.div>
                  </div>

                  {/* Problem */}
                  <p className="text-muted-foreground/70 text-[15px] line-through decoration-muted-foreground/20 decoration-[1.5px] mb-5 leading-relaxed">
                    {item.problem}
                  </p>

                  {/* Divider with arrow */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-primary">
                        <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="h-px flex-1 bg-gradient-to-r from-primary/20 to-transparent" />
                  </div>

                  {/* Solution */}
                  <p className="text-foreground font-bold text-base sm:text-[17px] leading-relaxed">
                    {item.solution}
                  </p>
                </div>
              </div>

              {/* Hover shadow boost */}
              <div
                className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"
                style={{ boxShadow: "0 16px 48px -8px hsl(var(--primary) / 0.12)" }}
              />
            </motion.div>
          ))}
        </div>

        {/* Bottom tagline */}
        <ScrollReveal className="text-center mt-16">
          <motion.p
            className="text-muted-foreground/60 text-sm sm:text-base italic max-w-lg mx-auto leading-relaxed"
            whileInView={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            viewport={{ once: true }}
          >
            ✨ {t('problem_solution.bottom_line')}
          </motion.p>
        </ScrollReveal>
      </div>
    </section>
  );
};
